import { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, useRoutes, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@/app/providers';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from 'sonner';
import { createAppRoutes } from '@/app/routes';
import { setUnauthorizedHandler } from '@/lib/api/client';
import { checkAuth, logout as authLogout } from '@/services/authService';

function AppRoutes() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  // Check authentication on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const authenticated = await checkAuth();
      setIsAuthenticated(authenticated);
      setIsChecking(false);
    };

    verifyAuth();
  }, []);

  // Setup 401 handler for API client
  useEffect(() => {
    const handleUnauthorized = () => {
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
    };

    setUnauthorizedHandler(handleUnauthorized);
  }, [navigate]);

  const handleLogin = useCallback(() => {
    setIsAuthenticated(true);
    navigate('/dashboard');
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      await authLogout();
    } catch {
      // Ignore error - will be logged out anyway
    }
    setIsAuthenticated(false);
    navigate('/login');
  }, [navigate]);

  const routes = useMemo(
    () => createAppRoutes(isAuthenticated, handleLogout, handleLogin),
    [isAuthenticated, handleLogout, handleLogin]
  );

  const routing = useRoutes(routes);

  // Show loading screen while checking authentication
  if (isChecking) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600 dark:border-gray-700 dark:border-t-blue-500" />
          <p className="text-gray-600 dark:text-gray-400">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return routing;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton theme="light" />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
